<script setup lang="ts">
  import { inject, useCssModule } from 'vue';

  import { RADIO_GROUP_INJECTION } from '../RadioGroup.keys';

  const radioGroupInjection = inject(RADIO_GROUP_INJECTION.key);

  if (!radioGroupInjection) {
    throw new Error('VariantTile must be used with a RadioGroup instance.');
  }

  const { name, disabled, fullWidth, modelValue, options, update } = radioGroupInjection;
  const classes = useCssModule();
</script>

<template>
  <div class="flex flex-wrap" :class="classes.root">
    <label
      v-for="option in options"
      :key="`${name}-${option.id}`"
      :class="[classes['tile-container'], { 'w-full': fullWidth }]"
      :for="`${name}-${option.id}`"
    >
      <div
        class="flex border"
        :class="[
          classes['tile-header'],
          {
            'border-blue-500 bg-blue-100 text-ice-900': modelValue === option.value,
            'border-ice-500 bg-ice-100 text-ice-700': modelValue !== option.value,
          },
        ]"
      >
        <input
          :id="`${name}-${option.id}`"
          class="sr-only"
          type="radio"
          :name="name"
          :value="option.value"
          :checked="modelValue === option.value"
          :disabled="disabled || option.disabled"
          @input="update"
        />
        <div>
          <span>
            {{ option.text }}
          </span>
        </div>
      </div>
      <div
        class="border-x border-b"
        :class="[
          classes['tile-body'],
          {
            'border-ice-500': modelValue !== option.value,
            'border-blue-500': modelValue === option.value,
          },
        ]"
      >
        <p class="m-0 text-ice-900">{{ option.subTitle }}</p>
        <p class="m-0 text-ice-700">{{ option.subText }}</p>
      </div>
    </label>
  </div>
</template>

<style module>
  @reference "../../../../styles/main.css";

  @layer utilities {
    .root {
      gap: --spacing(6);
    }

    .root label {
      cursor: pointer;
      user-select: none;
    }

    .root input:disabled ~ label {
      cursor: auto;
    }

    .root input:hover:not(:disabled) ~ label {
      color: var(--color-ice-900);
    }

    .root input:not(:disabled) ~ label:hover {
      color: var(--color-ice-900);
    }

    .root label.tile-container {
      border-radius: var(--radius-sm);
      display: flex;
      flex: 1;
      flex-direction: column;
    }

    .tile-header,
    .tile-body {
      transition: all 0.2s;
    }

    .tile-header {
      border-radius: var(--radius-sm) var(--radius-sm) 0 0;
      padding: --spacing(3) 0;
    }

    .root label.tile-container:hover .tile-header {
      background-color: var(--color-blue-100) !important;
      border-color: var(--color-blue-500) !important;
      color: var(--color-ice-900) !important;
      transition: all 0.2s;
    }

    .tile-body {
      border-radius: 0 0 var(--radius-sm) var(--radius-sm);
      background-color: var(--color-white);
      padding: --spacing(6);
      display: flex;
      flex-direction: column;
    }

    .root label.tile-container:hover .tile-body {
      border-color: var(--color-blue-500) !important;
      transition: all 0.2s;
    }

    .root input {
      appearance: none;
      background-color: var(--color-white);
      border-radius: 50%;
      width: 20px;
      height: 20px;
      border: 1px solid var(--color-ice-500);
      transition: all 0.2s;
      position: relative;
      cursor: pointer;
      top: 2px;
    }

    .root input ~ div {
      font-weight: var(--font-weight-medium);
      user-select: none;
      cursor: pointer;
      padding: 0 --spacing(3);
    }

    .root input:disabled {
      background: #eef2f4;
    }

    .root input:checked {
      background-image: radial-gradient(var(--color-blue-500) 50%, transparent 54%);
    }

    .root label.tile-container input {
      margin-left: --spacing(3);
    }

    .root input:disabled:checked {
      background-image: radial-gradient(var(--color-ice-500) 50%, transparent 54%);
    }

    @media screen and (width <= 640px) {
      .root {
        flex-direction: column;
      }
    }
  }
</style>
